import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ICard } from '..'
import type { AxiosError } from 'axios'
import type { ReactElement } from 'react'
import type { IGetMlResultError, IGetMlResultQueryOptions } from '~/types/catalog'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import { AUTHORIZATIONS, FORM_ERRORS, TOAST_ERRORS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useGeneratePostUsingMlMutation, useMlResultQuery } from '~/queries/catalog'
import { useFileDetailsQuery } from '~/queries/files'
import { displayToast } from '~/utils/toast'

interface FormValues {
  prompt: string
}

const validationSchema = Yup.object().shape({
  prompt: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const defaultValues: FormValues = {
  prompt: '',
}

const defaultMlResultQueryOptions: IGetMlResultQueryOptions = {
  enabled: false,
  refetchInterval: false,
}

interface Props {
  addToCards: (newPost: ICard) => void
}

const GenerateWithMlForm = ({ addToCards }: Props): ReactElement => {
  /** Local state */

  const isUserWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.CCC.CATALOG.WRITE])

  const [currentCard, setCurrentCard] = useState<ICard>()

  const [mlRequestId, setMlRequestId] = useState('')

  const [options, setOptions] = useState<IGetMlResultQueryOptions>(defaultMlResultQueryOptions)

  const [previewFileId, setPreviewFileId] = useState('')

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = methods

  /** Queries & Mutations */

  const { data: fileDetailsData } = useFileDetailsQuery(previewFileId)

  const { mutateAsync: generatePostUsingMlMutation } = useGeneratePostUsingMlMutation()

  const {
    data: mlResultData,
    error: mlResultError,
    isError: isMlResultError,
    isFetching: isMlResultFetching,
    isLoading: isMlResultLoading,
    isSuccess: isMlResultSuccess,
  } = useMlResultQuery(mlRequestId, options)

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    if (!isUserWriteAuthorized) {
      displayToast(TOAST_ERRORS.NOT_AUTHORIZED, 'error')
      return
    }

    if (values.prompt) {
      try {
        const { ml_request_id } = await generatePostUsingMlMutation({ prompt: values.prompt })
        setMlRequestId(ml_request_id)
        setOptions({ enabled: true, refetchInterval: 2500 })
      } catch (e) {
        captureException(e)
        displayToast('Post could not be created', 'error')
      }
    }
  }

  /** Effects */

  useEffect(() => {
    if (mlResultData) {
      setCurrentCard({
        categories: mlResultData.categories,
        coloringTemplateId: mlResultData.coloring_template_id,
        pdfFileId: mlResultData.pdf_file_id,
        previewPngFileId: mlResultData.preview_png_file_id,
        prompt: mlResultData.cleaned_prompt,
        templateOrigin: mlResultData.origin_type,
      })
      setPreviewFileId(mlResultData.preview_png_file_id)
    }
  }, [mlResultData])

  useEffect(() => {
    if (currentCard && fileDetailsData) {
      addToCards({ ...currentCard, mediaUrl: fileDetailsData.download_url })
      setCurrentCard(undefined)
    }
  }, [addToCards, currentCard, fileDetailsData])

  useEffect(() => {
    if (isMlResultSuccess) {
      displayToast('Post successfully created', 'success')
      setOptions(defaultMlResultQueryOptions)
      reset()
    }
  }, [isMlResultSuccess, mlRequestId, reset])

  /** Others */

  const isCooking =
    (isMlResultFetching && isMlResultLoading) ||
    (isMlResultError && ((mlResultError as AxiosError).response?.data as IGetMlResultError).kind === 'processing')

  return (
    <>
      <form className="generate-with-ml__prompt-area" onSubmit={handleSubmit(handleFormSubmit)}>
        <TextInput
          {...register('prompt')}
          error={errors.prompt?.message}
          placeholder="Enter a prompt"
          readOnly={isSubmitting || isCooking}
        />
        <Button className="submit-button" disabled={isSubmitting || !isDirty || isCooking} type="submit">
          {isCooking ? '⏳' : 'Submit'}
        </Button>
      </form>
    </>
  )
}

export default GenerateWithMlForm
